import { h } from 'preact'; /** @jsx h */
import { useContext, useEffect, useState } from 'preact/hooks';
import { NotificationContext } from '../context/NotificationContext';

import { getOrderById } from '../../forms/apiFunctions';
import { fetchProducts } from '../lib/fetchProducts';
import formatPrice from '../utils/formatPrice';

import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import ExtraContribution from '../components/ExtraContribution/ExtraContribution';
import Orderline from '../components/Orderline/Orderline';
import NotificationBanner from '../components/ProductHero/NotificationBanner/NotificationBanner';
import TaxReduction from '../components/TaxReduction/TaxReduction';
import UserInfo from '../components/UserInfo/UserInfo';

import '../styles/orderConfirmationPage.scss';

const OrderConfirmationPage = ({ slug }) => {
    const { showNotification } = useContext(NotificationContext);

    // User state
    const [user, setUser] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderlines, setOrderlines] = useState(null);
    const [products, setProducts] = useState(null);
    const [prodnrArray, setProdnrArray] = useState(null);
    const [total, setTotal] = useState(0);
    const [taxDeduction, setTaxDeduction] = useState(false);
    const [orderDate, setOrderDate] = useState(null);
    const [isStillTTL, setIsStillTTL] = useState(false);
    const [showtimeout, setShowtimeout] = useState(false);

    const [contributionValue, setContributionValue] = useState(0);
    const [viewContribution, setViewContribution] = useState(true);
    const [viewContributionInput, setViewContributionInput] = useState(false);

    // Check if user is logged in and if orderid is set in window.NLA
    useEffect(() => {
        if (window.NLA?.user) {
            const userFromNLA = window.NLA.user;
            setUser(userFromNLA);
        } else {
            setUser(null);
        }

        if (window.NLA?.orderId) {
            const orderId = window.NLA.orderId;
            setOrderId(orderId);
        } else {
            setOrderId(null);
        }
    }, []);

    // Fetch order details and prodnrs with getOrderById({order_id: orderId}), then fetch products with fetchProducts(prodnr)
    useEffect(() => {
        if (orderId) {
            getOrderById({ ordre_id: orderId }).then(res => {
                // Set orderDetails
                setOrderDetails(res);

                // Get linjenr, prod_nr and antall from orderDetails linje and create an array of objects
                const orderlines = res?.linje?.map(item => ({
                    linenr: item.linnr,
                    prodnr: item.prod_nr,
                    qty: item.antall,
                    price: parseFloat(item.belop.replace(',', '')),
                    shipmentType:
                        item.leveranse?.sendes_posten === 'J'
                            ? 'Gavebevis i posten'
                            : item.leveranse?.sendes_epost === 'J'
                            ? 'Gavebevis på e-post'
                            : ''
                }));

                // Set orderlines
                setOrderlines(orderlines);

                // Array of prodnrs from orderlines to use in fetchProducts. Remove duplicates
                const prodnrArray = [...new Set(orderlines?.map(item => item.prodnr))];
                setProdnrArray(prodnrArray);

                // Get ordre_dato and ordre_tid from orderDetails and create a date object
                const dateObject = new Date(
                    res.ordre_dato + ' ' + res.ordre_tid?.replace('.', ':')
                );
                setOrderDate(dateObject);

                // If user is logged in and has no email, set email temporarily from orderDetails
                if (user && user?.ho_epost === '') {
                    setUser({ ...user, ho_epost: res.ordre_epost });
                }
            });
        }
    }, [orderId]);

    // Fetch products with prodnrArray
    useEffect(() => {
        if (prodnrArray) {
            fetchProducts(prodnrArray).then(res => {
                setProducts(res);
            });
        }
    }, [prodnrArray]);

    // Check if order is tied to user
    useEffect(() => {
        if (orderDetails && user) {
            const orderMedlnr = `${orderDetails.firmanr} ${orderDetails.medlemsnr}`;
            const userMedlnr = user.medlemsnr;
            if (orderMedlnr === userMedlnr) {
                setOrderSuccess(true);
            }
        }
    }, [orderDetails, user]);

    // Add or overwrite properties in orderlines from info in products. Match on prodnr / id
    useEffect(() => {
        if (orderlines && products) {
            const updatedOrderlines = orderlines.map(orderline => {
                const product = products?.find(
                    product => product.id === parseInt(orderline.prodnr)
                );
                return { ...product, ...orderline };
            });
            setOrderlines(updatedOrderlines);

            if (window._paq) {
                // Track orderlines with Matomo
                updatedOrderlines.forEach(orderline => {
                    let price = orderline.donation || orderline.inputPrice || orderline.price;
                    const qty = parseInt(orderline.qty);

                    if (typeof price === 'string') {
                        price = price.replace(',', '');
                        price = parseFloat(price);
                    }

                    const delivery = orderline.shipmentType === 'Gavebevis på e-post' ? 'D' : 'F';
                    const uniqueId = `${orderline.id}-${delivery}-${price / qty}`;

                    window._paq.push([
                        'addEcommerceItem',
                        uniqueId, // Product SKU
                        orderline.name, // Product Name
                        orderline.catName, // Product Category
                        price / qty, // Price
                        qty // Quantity
                    ]);
                });

                // Track order with Matomo
                window._paq.push([
                    'trackEcommerceOrder',
                    orderId, // Order ID
                    total, // Order Total
                    0, // Subtotal
                    0, // Tax
                    0, // Shipping
                    false // Discount
                ]);
            }

            // Check if cart contains products with tax deduction
            const taxDeduction = updatedOrderlines?.some(line => line.taxDeduction === true);
            setTaxDeduction(taxDeduction);
        }
    }, [products]);

    // Calculate total price of orderlines
    useEffect(() => {
        let calculatedTotal = 0;
        orderlines?.forEach(orderline => {
            calculatedTotal += parseInt(orderline.price);
        });

        setTotal(calculatedTotal);
    }, [orderlines]);

    // Show orderDetails based on orderDate compared to current date
    useEffect(() => {
        if (orderDate) {
            const currentDate = new Date();
            const expirationDate = new Date(orderDate);
            expirationDate.setDate(orderDate.getDate() + 1);

            if (currentDate < expirationDate) {
                setIsStillTTL(true);
            }
        }
    }, [orderDate]);

    useEffect(() => {
        if (!orderSuccess || !isStillTTL) {
            setShowtimeout(true);
        } else {
            setShowtimeout(false);
        }
    }, [orderSuccess, isStillTTL]);

    // If contributionValue is greater than 0, add item to cart
    useEffect(() => {
        if (contributionValue > 0) {
            // Add item to cart
            addContributionToCart();
            // Update the cart count in the header
            window.updateCartCount();
        }
    }, [contributionValue]);

    // Add contribution item with id 150 to snla-cart in localstorage with contributionValue as price
    const addContributionToCart = () => {
        const cartFromStorage = JSON.parse(localStorage.getItem('snla-cart'));
        if (cartFromStorage) {
            const contributionInCart = cartFromStorage.find(item => item.id === 150);
            if (contributionInCart) {
                const updatedCart = cartFromStorage.map(item => {
                    if (item.id === 150) {
                        return { ...item, donation: contributionValue };
                    } else {
                        return item;
                    }
                });
                localStorage.setItem('snla-cart', JSON.stringify(updatedCart));
            } else {
                const updatedCart = [
                    ...cartFromStorage,
                    { id: 150, qty: 1, donation: contributionValue }
                ];
                localStorage.setItem('snla-cart', JSON.stringify(updatedCart));
            }
        } else {
            const updatedCart = [{ id: 150, qty: 1, donation: contributionValue }];
            localStorage.setItem('snla-cart', JSON.stringify(updatedCart));
        }
        // TODO: Show added to cart message
        showNotification(
            'success',
            `1 × Ekstra gave er lagt til i handlevognen din.`,
            'Vis handlevogn',
            '/nettbutikk/handlevogn',
            10
        );
    };

    // Handle keypress on contribution input to only allow numbers
    const handleKeyPress = e => {
        const keyCode = e.keyCode || e.which;
        const keyValue = String.fromCharCode(keyCode);
        if (!/^\d+$/.test(keyValue)) {
            e.preventDefault();
        }
    };

    // Check if contribution item with id 150 is in snla-cart in localstorage. If so, set viewContribution to false
    useEffect(() => {
        const cartFromStorage = JSON.parse(localStorage.getItem('snla-cart'));
        if (cartFromStorage) {
            const contributionInCart = cartFromStorage.find(item => item.id === 150);
            if (contributionInCart) {
                setViewContribution(false);
            } else {
                setViewContribution(true);
            }
        } else {
            setViewContribution(true);
        }
    }, [contributionValue]);

    // Check url params for error message
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        if (error) {
            // eslint-disable-next-line
            console.log('API ERROR: ', error);
            showNotification(
                'error',
                'En feil har oppstått. Du vil motta ordrebekreftelse på e-post. Hvis problemet vedvarer, venligst ta kontakt med oss på e-post.',
                '',
                '',
                10
            );
        }
    }, []);

    // Remove items from cart in localstorage and empty user details in localstorage
    useEffect(() => {
        if (orderId && orderlines) {
            // Match items in orderlines with items in localstorage cart and remove them
            const cartFromStorage = JSON.parse(localStorage.getItem('snla-cart'));

            if (cartFromStorage) {
                const updatedCart = cartFromStorage.filter(cartItem => {
                    const orderline = orderlines?.find(
                        orderline =>
                            parseInt(orderline.prodnr) === cartItem.id &&
                            parseInt(orderline.qty) === cartItem.qty &&
                            (orderline.shipmentType === cartItem.shipmentType ||
                                !cartItem.shipmentType) &&
                            (cartItem.inputPrice
                                ? parseInt(orderline.price / orderline.qty) === cartItem.inputPrice
                                : cartItem.donation
                                ? parseInt(orderline.price / orderline.qty) ===
                                  parseInt(cartItem.donation)
                                : false)
                    );
                    return !orderline;
                });

                localStorage.setItem('snla-cart', JSON.stringify(updatedCart));
            }
        }

        // Remove user details from localstorage
        localStorage.removeItem('snla-user');

        // Update the cart count in the header
        window.updateCartCount();
    }, [orderId, orderlines]);

    return (
        <div className="ws-reciept">
            <Breadcrumbs slug={slug} />
            <div className="ws-reciept-wrapper">
                <div className="ws-title-container">
                    <h1 className="ws-title">Tusen takk for ditt bidrag!</h1>
                    <p>
                        Vårt arbeid handler om å nå ut med best mulig behandling, raskest mulig, og
                        din støtte gjør det mulig å fortsette dette arbeidet. Det er et bidrag som
                        kort sagt redder liv. Tusen takk.
                    </p>
                </div>

                {/* If not logged in or the order is not tied to the user or the isStillTTL has expired */}
                {showtimeout && (
                    <div className="ws-timeout-container">
                        <h3 className="ws-timeout-title">
                            Av sikkerhetsgrunner kan du ikke lenger se ordrebekreftelsen din her.
                        </h3>
                        <p className="ws-timeout-text">
                            For å se bestillingen din, vennligst logg inn på &quot;Min side&quot;
                            eller sjekk e-posten din. Vi har sendt deg en e-post med
                            ordrebekreftelsen.
                        </p>
                    </div>
                )}

                {/* If logged in and the order is tied to the user and isStillTTL is true */}
                {orderSuccess && isStillTTL && (
                    <div className="ws-reciept-container">
                        <div className="ws-reciept-text">
                            <p>Du vil straks motta en bekreftelse på e-post.</p>
                        </div>

                        {/* User details */}
                        <div className="ws-reciept-user-details">
                            <UserInfo user={user} setUser={setUser} isReciept />
                        </div>

                        {/* Orderlines */}
                        <div className="ws-reciept-orderlines">
                            <h3 className="ws-orderlines-title">Dine gaver</h3>
                            <div className="ws-orderlines-container">
                                {orderlines?.length > 0 &&
                                    orderlines?.map(item => (
                                        <Orderline key={item.id} item={item} />
                                    ))}
                            </div>

                            {/* Total sum */}
                            <div className="ws-orderlines-total">
                                {orderlines?.length > 0 && (
                                    <h3 className="ws-total-sum">Sum {formatPrice(total)},-</h3>
                                )}
                            </div>
                        </div>

                        {/* Tax deduction */}
                        {taxDeduction && (
                            <div className="ws-reciept-tax-deduction">
                                <TaxReduction
                                    viewTaxDeduction={true}
                                    setViewTaxDeduction={false}
                                    isReciept
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* If contributionValue is greater than 0, show contribution */}
                {viewContribution && (
                    <div className="ws-reciept-contribution">
                        <ExtraContribution
                            setContributionValue={setContributionValue}
                            viewContributionInput={viewContributionInput}
                            setViewContributionInput={setViewContributionInput}
                            handleKeyPress={handleKeyPress}
                            isReciept
                        />
                    </div>
                )}
            </div>

            {/* Notification banner for success or error */}
            <NotificationBanner />
        </div>
    );
};

export default OrderConfirmationPage;
